/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@apollo/client';
import type { CourseraTierSubscriptions_EndRazorpaySubscriptionForCourseraTierProductInput as EndRazorpaySubscriptionForCourseraTierProductInput } from '__generated__/graphql-types';

import logger from 'js/app/loggerSingleton';
import { isRightToLeft } from 'js/lib/language';
import { useRetracked } from 'js/lib/retracked';

import { Dialog, Typography2 } from '@coursera/cds-core';

import { PRODUCT_TYPE_NAMES } from 'bundles/browse-common/constants';
import EndRazorpaySubscriptionForCourseraTierProduct from 'bundles/coursera-plus-common/api/EndRazorpaySubscriptionForCourseraTierProduct.graphql';
import type { EndRazorpaySubscriptionForCourseraTierProductMutation as EndRazorpaySubscriptionResult } from 'bundles/coursera-plus-common/api/__generated__/EndRazorpaySubscriptionForCourseraTierProduct';
import { SizedImage } from 'bundles/coursera-plus-common/components/subscriptionTiers/subscriptionTiersUtils';
import StepOneIndiaUpgradeModal from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IndiaUpgrade/StepOneIndiaUpgradeModal';
import StepTwoIndiaUpgradeModal from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IndiaUpgrade/StepTwoIndiaUpgradeModal';
import {
  getCopyForStepOneIndiaTiersTest,
  getCopyForStepTwoModalIndiaTiersTest,
  getStepCopy,
} from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/upgradeCopies';
import courseraPlusProductVariants from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import { logo } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import {
  saveRedirectUrlAfterUpgrade,
  setIndiaTiersUpgradeData,
} from 'bundles/coursera-plus-common/utils/subscriptionTiersUtils';
import { useExistingCourseraLiteSubscription } from 'bundles/coursera-plus-common/utils/withExistingCourseraLiteSubscription';
import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import type { SubmitEnrollmentPromiseOptions } from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { ProfessionalCertificateS12n } from 'bundles/s12n-common/constants/s12nProductVariants';
import { GUIDED_PROJECT, RHYME_PROJECT, SELF_PACED_PROJECT } from 'bundles/search-common/constants/entityTypes';

import _t from 'i18n!nls/coursera-plus-common';

const POSTGRADUATE_DIPLOMA_ENTITY_NAME = 'POSTGRADUATE DIPLOMA';

const GRADUATE_CERTIFICATE_ENTITY_NAME = 'GRADUATE CERTIFICATE';

const UNIVERSITY_CERTIFICATE_ENTITY_NAME = 'UNIVERSITY CERTIFICATE';

const capitalizeFirstLetterEachWord = (str: string | undefined): string | undefined => {
  if (!str) {
    return undefined;
  }
  return str.replace(/\w\S*/g, (word) => word.replace(/^\w/, (char) => char.toUpperCase()));
};

const getProductDisplayTypeName = (productTypeName?: string | null) => {
  if (productTypeName === SELF_PACED_PROJECT) {
    return PRODUCT_TYPE_NAMES.SELF_PACED_PROJECT;
  } else if (productTypeName === GUIDED_PROJECT || productTypeName === RHYME_PROJECT) {
    return PRODUCT_TYPE_NAMES.GUIDED_PROJECT;
  }
  return productTypeName;
};

export const getTranslatedProductName = (productType?: string | null) => {
  const productName = getProductDisplayTypeName(productType);
  const productNameAllCapitalize = productName?.toUpperCase() || productName;
  const prettyProductName = capitalizeFirstLetterEachWord(productName?.toLowerCase());
  switch (productNameAllCapitalize) {
    case PRODUCT_TYPE_NAMES.COURSE:
      return _t('Course');
    case PRODUCT_TYPE_NAMES.GUIDED_PROJECT:
      return _t('Guided Project');
    case PRODUCT_TYPE_NAMES.SELF_PACED_PROJECT:
      return _t('Project');
    case PRODUCT_TYPE_NAMES.SPECIALIZATION:
      return _t('Specialization');
    case PRODUCT_TYPE_NAMES.CERTIFICATE:
      return _t('Professional Certificate');
    case PRODUCT_TYPE_NAMES.MASTERTRACK:
      return isRightToLeft(_t.getLocale())
        ? _t('#{R}MasterTrack Certificate', { R: String.fromCharCode(174) })
        : _t('MasterTrack#{R} Certificate', { R: String.fromCharCode(174) });
    case PRODUCT_TYPE_NAMES.DEGREE:
      return _t('Degree');
    case POSTGRADUATE_DIPLOMA_ENTITY_NAME:
      return _t('Post Graduate');
    case GRADUATE_CERTIFICATE_ENTITY_NAME:
      return _t('Graduate Certificate');
    case UNIVERSITY_CERTIFICATE_ENTITY_NAME:
      return _t('University Certificate');
    default:
      return prettyProductName || productName;
  }
};

const styles = {
  courseraPlusLogo: css`
    display: block;
    margin: 0 0 16px 48px;
  `,
  stepCopy: css`
    position: absolute;
    top: 24px;
    left: 48px;
    color: var(--cds-color-grey-500);
  `,
};

type EndRazorpaySubscriptionInput = {
  input: EndRazorpaySubscriptionForCourseraTierProductInput;
};

type Props = {
  onClose: () => void;
};

const IndiaPriceTestUpgradeModal = ({ onClose }: Props) => {
  const [step, setStep] = useState(1);
  const copy = getStepCopy(step);
  const [ctaState, setCtaState] = useState<'loading' | 'failed' | undefined>(undefined);
  const retrackedTrack = useRetracked();
  const { course, s12n, courseraPlusMonthlyProductPrice, enrollmentAvailableChoices } = usePageData();
  const hasCourseraPlusFreeTrial = enrollmentAvailableChoices.canSubscribeToCourseraPlusFreeTrial;
  const { existingCourseraLiteSubscription } = useExistingCourseraLiteSubscription();
  const [endRazorpaySubscriptionForCourseraTierProduct] = useMutation<
    EndRazorpaySubscriptionResult,
    EndRazorpaySubscriptionInput
  >(EndRazorpaySubscriptionForCourseraTierProduct);

  if (!existingCourseraLiteSubscription || !courseraPlusMonthlyProductPrice) {
    return null;
  }

  let productTypeName: string = PRODUCT_TYPE_NAMES.COURSE;
  if (s12n) {
    if (s12n.productVariant === ProfessionalCertificateS12n) {
      productTypeName = PRODUCT_TYPE_NAMES.CERTIFICATE;
    } else {
      productTypeName = PRODUCT_TYPE_NAMES.SPECIALIZATION;
    }
  }
  const translatedProductTypeName: string = getTranslatedProductName(productTypeName) as string;

  const stepOneCopy = getCopyForStepOneIndiaTiersTest({
    courseraPlusProductPrice: courseraPlusMonthlyProductPrice,
    hasCourseraPlusFreeTrial,
    translatedProductTypeName,
    s12n,
  });
  const stepTwoCopy = getCopyForStepTwoModalIndiaTiersTest(translatedProductTypeName);

  const handleUpgradeToPlus = () => {
    setCtaState('loading');
    endRazorpaySubscriptionForCourseraTierProduct({
      variables: {
        input: {
          subscriptionId: existingCourseraLiteSubscription?.id,
        },
      },
      context: {
        clientName: 'gatewayGql',
      },
    })
      .then(() => {
        saveRedirectUrlAfterUpgrade();
        setIndiaTiersUpgradeData({
          liteSubscriptionId: existingCourseraLiteSubscription?.id,
          s12nId: s12n?.id,
          courseId: course?.id,
        });
        const handleSubmitPromise = choiceTypeToHandleSubmitPromise[EnrollmentChoiceTypes.SUBSCRIBE_TO_COURSERA_PLUS];
        const options: SubmitEnrollmentPromiseOptions = {
          productItemId: courseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL,
          s12nId: s12n?.id,
          courseId: course?.id,
        };
        submitEnrollmentPromise({ handleSubmitPromise, options });
      })
      .catch((error) => {
        setCtaState('failed');
        logger.error(error);
      });
  };

  const handleClose = () => {
    onClose();
    retrackedTrack({
      trackingData: { s12nId: s12n?.id, courseId: course?.id },
      trackingName: 'india_tiers_upgrade_modal_close',
      action: 'click',
    });
  };

  return (
    <Dialog aria-describedby="dialog-content" open variant="standard" onClose={handleClose}>
      <Typography2 component="p" variant="bodySecondary" css={styles.stepCopy}>
        {copy.step}
      </Typography2>
      <span css={styles.courseraPlusLogo}>
        <SizedImage src={logo.PLUS_FULL_BLUE} width={159} height={16} alt={copy.courseraPlus} />
      </span>
      {step === 1 && (
        <StepOneIndiaUpgradeModal s12n={s12n} course={course} goToNextStep={() => setStep(2)} copy={stepOneCopy} />
      )}
      {step === 2 && (
        <StepTwoIndiaUpgradeModal copy={stepTwoCopy} handleUpgradeToPlus={handleUpgradeToPlus} ctaState={ctaState} />
      )}
    </Dialog>
  );
};

export default IndiaPriceTestUpgradeModal;
